/**
 * brochureVectorModerno.js — Layout MODERNO in vector PDF (jsPDF).
 *
 * Stile: bold sans uppercase, magazine cover, statement tipografico.
 * Cover con titolo HUGE uppercase, banda accent solida in fondo.
 * Inner pages con grid stretto e numeri grossi.
 */
import { A4_W, A4_H, setFill, setStroke, setText, safe, getAttrs, cleanCity, loadImageAsJpegDataUrl, loadAgencyLogo, addPhotoCover, shortenEmail, cityIfNotInTitle } from "./brochurePdfShared";

export async function buildCoverModerno(pdf, data, col, showPrice = true) {
  // Foto 65% in alto
  // iter v28 r16 — alta risoluzione + cover-fit
  const hero = await loadImageAsJpegDataUrl(data.photos.hero, 2400);
  const photoH = A4_H * 0.62;
  if (hero) await addPhotoCover(pdf, hero, 0, 0, A4_W, photoH);
  else { setFill(pdf, "#DDD8CB"); pdf.rect(0, 0, A4_W, photoH, "F"); }

  // Banda accent solida sotto la foto
  setFill(pdf, col.accent);
  pdf.rect(0, photoH, A4_W, A4_H - photoH, "F");

  // Logo + agency in alto a sinistra SOPRA la foto (con shadow)
  // iter v28r7: bg foto scura → tint white se mono
  // iter v28r8: logo ingrandito 14 → 22mm
  let topX = 16;
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, true);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(22 / lw, 22 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", topX, 14, dw, dh);
      topX += dw + 5;
    }
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  setText(pdf, "#FAFAF7");
  const ag = safe(data.agencyName, "").toUpperCase();
  if (ag) {
    // Shadow
    setText(pdf, "#000000");
    pdf.setGState(new pdf.GState({ opacity: 0.5 }));
    pdf.text(ag, topX + 0.3, 22.3);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
    setText(pdf, "#FAFAF7");
    pdf.text(ag, topX, 22);
  }

  // Badge "NUOVO" top-right white pillola
  setFill(pdf, "#FAFAF7");
  pdf.roundedRect(A4_W - 36, 14, 22, 8, 1, 1, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, col.accent);
  pdf.text("NUOVO", A4_W - 32, 19.5);

  // Titolo HUGE UPPERCASE statement sulla banda accent
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  setText(pdf, "#FAFAF7");
  const title = safe(data.title, "Immobile").toUpperCase();
  const titleLines = pdf.splitTextToSize(title, A4_W - 32);
  let ty = photoH + 22;
  titleLines.slice(0, 3).forEach((line) => {
    pdf.text(line, 16, ty);
    ty += 10;
  });

  // Città (solo se non duplicata nel titolo)
  const cityShow = cityIfNotInTitle(data);
  if (cityShow) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.85 }));
    pdf.text(cityShow.toUpperCase(), 16, ty + 4);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Prezzo + meta in basso big
  if (showPrice) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    setText(pdf, "#FAFAF7");
    pdf.text(safe(data.price, "—"), 16, A4_H - 18);
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.9 }));
  const meta = `${data.surface || "—"} MQ · ${data.rooms || "—"} LOC.`;
  const mw = pdf.getTextWidth(meta);
  pdf.text(meta, A4_W - 16 - mw, A4_H - 18);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}

export async function buildDescriptionModerno(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // Big numero pagina sx
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(60);
  setText(pdf, col.accent);
  pdf.setGState(new pdf.GState({ opacity: 0.15 }));
  pdf.text("02", 16, 50);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Section label uppercase accent
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("LA PROPRIETÀ", A4_W - 16 - pdf.getTextWidth("LA PROPRIETÀ"), 24);

  // Titolo grande UPPERCASE
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  setText(pdf, "#1C1C1A");
  const title = safe(data.title, "Immobile").toUpperCase();
  const titleLines = pdf.splitTextToSize(title, A4_W - 32);
  let ty = 60;
  titleLines.slice(0, 2).forEach((line) => {
    pdf.text(line, 16, ty);
    ty += 9;
  });

  // Body description
  const desc = (data.description || data.subtitle || "").trim();
  if (desc) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    setText(pdf, "#1C1C1A");
    const wrapped = pdf.splitTextToSize(desc.length > 700 ? desc.slice(0, 697) + "…" : desc, A4_W - 32);
    let dy = ty + 8;
    wrapped.forEach((line) => {
      if (dy > 170) return;
      pdf.text(line, 16, dy);
      dy += 5.2;
    });
  }

  // Foto a destra
  // iter v28 r16 — bump risoluzione 1400→2000 + cover-fit
  const photo = data.photos.p2 ? await loadImageAsJpegDataUrl(data.photos.p2, 2000) : null;
  const photoX = 16;
  const photoY = 180;
  const photoW = A4_W - 32;
  const photoH = 60;
  if (photo) await addPhotoCover(pdf, photo, photoX, photoY, photoW, photoH);
  else { setFill(pdf, "#F1F4ED"); pdf.rect(photoX, photoY, photoW, photoH, "F"); }

  // Attributes grid 2×3 con valori grossi (Archivo Black style → bold)
  const attrs = getAttrs(data);
  const attrY = photoY + photoH + 12;
  const cols = 3;
  const colW = (A4_W - 32) / cols;
  attrs.forEach(([k, v], i) => {
    const row = Math.floor(i / cols);
    const c = i % cols;
    const x = 16 + c * colW;
    const y = attrY + row * 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, col.accent);
    pdf.text(k, x, y);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    setText(pdf, "#1C1C1A");
    pdf.text(safe(v), x, y + 8);
  });

  drawFooterModerno(pdf, data, "02");
}

export async function buildGalleryModerno(pdf, data, col) {
  setFill(pdf, "#FAFAF7");
  pdf.rect(0, 0, A4_W, A4_H, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  setText(pdf, col.accent);
  pdf.text("GALLERIA", 16, 24);
  pdf.text("03", A4_W - 16 - pdf.getTextWidth("03"), 24);

  // Linea accent sotto header
  setStroke(pdf, col.accent);
  pdf.setLineWidth(0.6);
  pdf.line(16, 28, A4_W - 16, 28);

  // Contact sheet 3×2 con numeri "01" - "06" su pillola accent
  const urls = [data.photos.hero, data.photos.p2, data.photos.p3, data.photos.p4, data.photos.p5, data.photos.p6].filter(Boolean);
  // iter v28 r16 — bump risoluzione 1200→1600 + cover-fit (no più stretch
  // delle 6 foto in griglia 3×2)
  const photos = await Promise.all(urls.slice(0, 6).map((u) => loadImageAsJpegDataUrl(u, 1600)));
  const gridTop = 36;
  const gridBottom = A4_H - 22;
  const gridH = gridBottom - gridTop;
  const cols = 3;
  const rows = 2;
  const m = 16;
  const g = 3;
  const cw = (A4_W - 2 * m - g * (cols - 1)) / cols;
  const ch = (gridH - g * (rows - 1)) / rows;
  for (let i = 0; i < 6; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const x = m + c * (cw + g);
    const y = gridTop + r * (ch + g);
    if (photos[i]) await addPhotoCover(pdf, photos[i], x, y, cw, ch);
    else { setFill(pdf, "#F1F4ED"); pdf.rect(x, y, cw, ch, "F"); }
    // Numero "01"..."06" su pillola accent top-left della foto
    setFill(pdf, col.accent);
    pdf.roundedRect(x + 1.5, y + 1.5, 8, 5.5, 1, 1, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    setText(pdf, "#FAFAF7");
    pdf.text(String(i + 1).padStart(2, "0"), x + 3.2, y + 5.4);
  }

  drawFooterModerno(pdf, data, "03");
}

export async function buildContactsModerno(pdf, data, col, showProntoLabel = true) {
  // Bg accent solido
  setFill(pdf, col.accent);
  pdf.rect(0, 0, A4_W, A4_H, "F");

  // Logo top-left
  // iter v28r7: bg accent solido (chiaro/medio) → tint white se mono per leggibilità
  // iter v28r8: logo ingrandito 20 → 28mm
  if (data.agencyLogoUrl) {
    const logo = await loadAgencyLogo(data, 600, true);
    if (logo) {
      const lw = logo.w, lh = logo.h;
      const scale = Math.min(28 / lw, 28 / lh);
      const dw = lw * scale, dh = lh * scale;
      pdf.addImage(logo.dataUrl, "PNG", 16, 16, dw, dh);
    }
  }

  // Big "CONTATTACI" UPPERCASE in alto a destra
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(32);
  setText(pdf, "#FAFAF7");
  const heading = "CONTATTACI";
  const hw = pdf.getTextWidth(heading);
  pdf.text(heading, A4_W - 16 - hw, 40);

  // Linea bianca divider
  setStroke(pdf, "#FAFAF7");
  pdf.setLineWidth(0.6);
  pdf.setGState(new pdf.GState({ opacity: 0.5 }));
  pdf.line(16, 56, A4_W - 16, 56);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  // Agenzia name BIG UPPERCASE
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  setText(pdf, "#FAFAF7");
  const ag = safe(data.agencyName, "La tua agenzia").toUpperCase();
  const agLines = pdf.splitTextToSize(ag, A4_W - 32);
  agLines.slice(0, 2).forEach((line, i) => {
    pdf.text(line, 16, 80 + i * 9);
  });
  if (data.agencyWeb) {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    setText(pdf, "#FAFAF7");
    pdf.setGState(new pdf.GState({ opacity: 0.85 }));
    pdf.text(data.agencyWeb.toUpperCase(), 16, 80 + agLines.length * 9 + 4);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }

  // Agente section bottom
  const agentY = A4_H - 90;
  if (data.agentPhoto) {
    // iter v28 r16 — bump risoluzione + cover-fit (centra volto)
    const ph = await loadImageAsJpegDataUrl(data.agentPhoto, 900);
    if (ph) {
      const r = 16;
      const cx = 16 + r;
      const cy = agentY + r;
      setFill(pdf, "#FAFAF7");
      pdf.circle(cx, cy, r, "F");
      const innerSize = (r - 1.5) * 2;
      await addPhotoCover(pdf, ph, cx - r + 1.5, cy - r + 1.5, innerSize, innerSize, { anchor: "top" });
    }
  }

  // Agent name + license
  const agentX = data.agentPhoto ? 50 : 16;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, "#FAFAF7");
  pdf.setGState(new pdf.GState({ opacity: 0.7 }));
  pdf.text("AGENTE DI RIFERIMENTO", agentX, agentY + 4);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  setText(pdf, "#FAFAF7");
  pdf.text(safe(data.agentName, "Agente").toUpperCase(), agentX, agentY + 14);

  // Contatti riga in fondo
  const cY = A4_H - 38;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  setText(pdf, "#FAFAF7");
  if (data.agentPhone) {
    pdf.setFontSize(7);
    pdf.setGState(new pdf.GState({ opacity: 0.7 }));
    pdf.text("TEL", 16, cY);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
    pdf.setFontSize(13);
    pdf.text(data.agentPhone, 16, cY + 6);
  }
  if (data.agentEmail) {
    pdf.setFontSize(7);
    pdf.setGState(new pdf.GState({ opacity: 0.7 }));
    pdf.text("EMAIL", A4_W / 2, cY);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
    pdf.setFontSize(11);
    pdf.text(shortenEmail(data.agentEmail, 28), A4_W / 2, cY + 6);
  }

  if (showProntoLabel) {
    pdf.setGState(new pdf.GState({ opacity: 0.4 }));
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(6.5);
    setText(pdf, "#FAFAF7");
    const wm = "PRONTOIMMO.IT";
    const wmW = pdf.getTextWidth(wm);
    pdf.text(wm, A4_W - 16 - wmW, A4_H - 14);
    pdf.setGState(new pdf.GState({ opacity: 1 }));
  }
}

function drawFooterModerno(pdf, data, num) {
  setStroke(pdf, "#1C1C1A");
  pdf.setLineWidth(0.3);
  pdf.setGState(new pdf.GState({ opacity: 0.4 }));
  pdf.line(16, A4_H - 18, A4_W - 16, A4_H - 18);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  setText(pdf, "#1C1C1A");
  pdf.setGState(new pdf.GState({ opacity: 0.7 }));
  const city = cleanCity(data.city);
  const label = [data.agencyName, city].filter(Boolean).join(" · ").toUpperCase();
  pdf.text(label, 16, A4_H - 12);
  const pageMark = `${num} · 04`;
  const pmW = pdf.getTextWidth(pageMark);
  pdf.text(pageMark, A4_W - 16 - pmW, A4_H - 12);
  pdf.setGState(new pdf.GState({ opacity: 1 }));
}
